import { useOutletContext } from "react-router-dom";
import { useLayoutEffect, useRef, useState } from "react";
import { UserInterface } from "./user/domain/interfaces/user_interface";
import { PartnerCompanyInterface } from "./user/domain/interfaces/company_interface";
import { useTranslation } from "../../i18n/useTranslation";

// Must match the gap-6 / pr-6 classes so the loop seam has the same spacing as between logos
const GAP_PX = 24;
const SCROLL_SPEED_PX_PER_SECOND = 40;

interface PartnersSectionProps {
    className?: string;
}

const PartnerLogos: React.FC<{ partners: PartnerCompanyInterface[]; hidden?: boolean }> = ({ partners, hidden }) => {
    const { t } = useTranslation();

    return partners.map(partner => (
        <img
            key={partner.id}
            src={partner.picture}
            alt={hidden ? '' : t('common.logo_alt', { name: partner.name })}
            title={partner.name}
            className="h-12 w-auto max-w-32 object-contain rounded-lg shrink-0"
        />
    ));
};

const PartnersSection: React.FC<PartnersSectionProps> = ({ className = '' }) => {
    const { t } = useTranslation();
    const { company } = useOutletContext<UserInterface>();
    const partners = company?.partners.filter(partner => partner.picture) ?? [];

    const containerRef = useRef<HTMLDivElement>(null);
    const logosRef = useRef<HTMLDivElement>(null);
    const [logosWidth, setLogosWidth] = useState(0);
    const [isOverflowing, setIsOverflowing] = useState(false);

    // Logos load asynchronously and the screen can be resized, so the overflow is re-measured on every size change
    useLayoutEffect(() => {
        const container = containerRef.current;
        const logos = logosRef.current;
        if (!container || !logos) return;

        const measure = () => {
            setLogosWidth(logos.offsetWidth);
            setIsOverflowing(logos.offsetWidth > container.clientWidth);
        };
        const observer = new ResizeObserver(measure);
        observer.observe(container);
        observer.observe(logos);
        return () => observer.disconnect();
    }, [partners.length]);

    if (partners.length === 0) return null;

    return (
        <section className={`flex flex-col items-center gap-4 self-stretch min-w-0 ${className}`}>
            <h3 className="text-black text-xl leading-5">{t('common.partners')}</h3>
            <div
                ref={containerRef}
                dir="ltr"
                className={`w-full overflow-hidden ${isOverflowing ? 'mask-[linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]' : ''}`}
            >
                <div
                    className={`flex w-max gap-6 ${isOverflowing ? 'pr-6 animate-partners-marquee' : 'mx-auto'}`}
                    style={isOverflowing ? { animationDuration: `${(logosWidth + GAP_PX) / SCROLL_SPEED_PX_PER_SECOND}s` } : undefined}
                >
                    <div ref={logosRef} className="flex items-center gap-6">
                        <PartnerLogos partners={partners}/>
                    </div>
                    {isOverflowing && (
                        <div className="flex items-center gap-6" aria-hidden="true">
                            <PartnerLogos partners={partners} hidden/>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default PartnersSection;
